// src/pages/course/UseingCoursePage.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CourseMapView from "../../components/course/CourseMapView";
import CourseRunningStatus from "../../components/course/CourseRunningStatus";
import {
  getLastMeasurement,
  getCompleteResult,
  giveupCourse,
} from "../../api/courseApi";
import { useToast } from "../../contexts/ToastContext";

function UseingCoursePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { course, workRecordId, userId } = location.state || {};

  // 중복 이동 방지
  const redirectedRef = useRef(false);

  const [measurement, setMeasurement] = useState({
    distance: 0,
    time: "00:00:00",
    speed: 0,
    heartRate: 0,
    calories: 0,
  });

  const safeNavigate = (to) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    navigate(to, { replace: true });
  };

  const handleCompleteResult = (result) => {
    if (!result || redirectedRef.current) return;

    // 백엔드 약속:
    // - OFF_ROUTE: targetId = courseId (상세로 복귀)
    // - GENERAL: targetId = workRecordId (운동기록 상세)
    // - BATTLE : targetId = battleId (대결 상세)
    if (result.type === "OFF_ROUTE") {
      showToast("경로를 이탈하셨습니다", "w");
      safeNavigate(`/course/${result.targetId}`);
      return;
    }

    if (result.type === "BATTLE") {
      safeNavigate(`/battle/getBattle?battleId=${result.targetId}`);
      return;
    }

    // 기본은 GENERAL로 취급
    safeNavigate(`/workrecord/get/${result.targetId}`);
  };

  // 잘못 진입 방어
  useEffect(() => {
    if (!workRecordId) {
      showToast("운동 정보가 없습니다. 다시 시도해 주세요.", "e");
      navigate("/course", { replace: true });
    }
  }, [workRecordId, navigate, showToast]);

  // completeResult 1회 체크 함수 (여러 곳에서 재사용)
  const checkCompleteOnce = async () => {
    if (!workRecordId || redirectedRef.current) return;

    try {
      const result = await getCompleteResult(workRecordId);
      handleCompleteResult(result);
    } catch (e) {
      const status = e?.response?.status;

      // 409 = 아직 운동중 (정상)
      if (status === 409) return;

      // 404 = 아직 결과 없음 or 백엔드 정책상 기록이 없어서 결과도 없음
      // 여기서는 "이탈"로 오판하면 안 되므로 아무것도 하지 않음
      if (status === 404) return;

      console.warn(e);
    }
  };

  // 마지막 측정값 폴링
  useEffect(() => {
    if (!workRecordId) return;

    let isUnmounted = false;

    const fetchLast = async () => {
      if (redirectedRef.current) return;

      try {
        const data = await getLastMeasurement(workRecordId);
        if (!data || isUnmounted) return;

        setMeasurement({
          distance: data.distance ?? 0,
          time: data.time ?? "00:00:00",
          speed: data.speed ?? 0,
          heartRate: data.heartRate ?? 0,
          calories: data.calories ?? 0,
        });
      } catch (e) {
        const status = e?.response?.status;

        // 측정이 404인 경우가 있어도, 그걸 즉시 이탈로 판단하면 안 됨
        // completeResult에서 OFF_ROUTE 결과가 나오면 그때 처리
        if (status === 404) {
          await checkCompleteOnce();
          return;
        }

        console.warn(e);
      }
    };

    fetchLast();
    const intervalId = setInterval(fetchLast, 3000);

    return () => {
      isUnmounted = true;
      clearInterval(intervalId);
    };
  }, [workRecordId]);

  // completeResult 폴링
  useEffect(() => {
    if (!workRecordId) return;

    let isUnmounted = false;

    const tick = async () => {
      if (isUnmounted) return;
      await checkCompleteOnce();
    };

    // 들어오자마자 1회 체크 + 주기 체크
    tick();
    const intervalId = setInterval(tick, 5000);

    return () => {
      isUnmounted = true;
      clearInterval(intervalId);
    };
  }, [workRecordId]);

  const handleGiveup = async () => {
    if (!workRecordId) return;

    const ok = window.confirm("정말 코스를 포기하시겠습니까?");
    if (!ok) return;

    try {
      await giveupCourse(workRecordId);
      showToast("코스를 포기했습니다.", "i");

      if (course?.courseId) {
        safeNavigate(`/course/${course.courseId}`);
      } else {
        safeNavigate("/course");
      }
    } catch (e) {
      console.warn(e);
      showToast("코스 포기에 실패했습니다.", "e");
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col max-w-md mx-auto relative">
      <header className="px-5 pt-6 pb-4 bg-white sticky top-0 z-20 shadow-sm flex items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-800">코스 사용 중</h1>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 pb-20 scrollbar-hide space-y-6">
        <section className="bg-white rounded-[24px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 h-64 relative">
          <CourseMapView
            pathData={course?.coursePathList}
            startLocation={course?.startPoint}
          />

          <div className="absolute top-3 left-3 z-10 bg-white/90 px-3 py-1.5 rounded-xl shadow-sm border border-gray-200 flex flex-col">
            <span className="text-[10px] font-bold text-gray-400">
              {course?.courseName || "코스"}
            </span>
            <span className="text-xs font-bold text-blue-500">주행 중</span>
          </div>
        </section>

        <CourseRunningStatus
          type={course?.type || "코스"}
          distance={measurement.distance}
          time={measurement.time}
          speed={measurement.speed}
          heartRate={measurement.heartRate}
          calorie={measurement.calories}
          onEndCourse={handleGiveup}
          buttonText="코스 포기"
        />
      </main>
    </div>
  );
}

export default UseingCoursePage;
